import React, { useState, useEffect } from 'react';
import { Search, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { confirmAction } from '../../utils/toastUtils.jsx';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import { Plus } from 'lucide-react';
import styles from '../Admins/Admins.module.css';


const TimePicker = ({ value, onChange }) => {
  let h = '09', m = '00', ampm = 'AM';
  if (value && value.includes(':')) {
    const parts = value.split(' ');
    const timeParts = parts[0].split(':');
    h = timeParts[0];
    m = timeParts[1] || '00';
    ampm = parts[1] || 'AM';
  }

  const hours = Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
      <select 
        value={h} 
        onChange={(e) => onChange(`${e.target.value}:${m} ${ampm}`)}
        style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
      >
        {hours.map(hour => <option key={hour} value={hour}>{hour}</option>)}
      </select>
      <span>:</span>
      <select 
        value={m} 
        onChange={(e) => onChange(`${h}:${e.target.value} ${ampm}`)}
        style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
      >
        {minutes.map(minute => <option key={minute} value={minute}>{minute}</option>)}
      </select>
      <select 
        value={ampm} 
        onChange={(e) => onChange(`${h}:${m} ${e.target.value}`)}
        style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
      >
        <option value='AM'>AM</option>
        <option value='PM'>PM</option>
      </select>
    </div>
  );
};

const BusinessPromoRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', category: 'Food & Catering', customCategory: '', description: '', services: '', location: '', phone: '', email: '', workingHours: '', website: '', instagram: '', facebook: '', ownerName: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const openAddDrawer = () => {
    setFormData({
      name: '', category: 'Food & Catering', customCategory: '', description: '', services: '', location: '', phone: '', email: '', workingHours: '', website: '', instagram: '', facebook: '', ownerName: ''
    });
    setImageFile(null);
    setLogoFile(null);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      if (imageFile) data.append('image', imageFile);
      if (logoFile) data.append('logo', logoFile);

      await api.post('/superadmin/businesses', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Business Promotion added successfully!');
      setIsDrawerOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to add promotion');
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/business-promo-requests');
      setRequests(data);
    } catch (error) {
      toast.error('Failed to fetch business promotion requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    confirmAction(`Are you sure you want to mark this request as ${newStatus}?`, async () => {
      try {
        await api.put(`/superadmin/business-promo-requests/${id}/status`, { status: newStatus });
        toast.success(`Request ${newStatus.toLowerCase()} successfully!`);
        fetchRequests();
      } catch (error) {
        toast.error(`Failed to update status`);
      }
    });
  };

  const handleDelete = async (id) => {
    confirmAction('Are you sure you want to delete this request?', async () => {
      try {
        await api.delete(`/superadmin/business-promo-requests/${id}`);
        toast.success('Request deleted successfully');
        fetchRequests();
      } catch (error) {
        toast.error('Failed to delete request');
      }
    });
  };

  const filteredRequests = requests.filter(req => 
    req.businessName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.adminsContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h2>Promo Requests</h2>
          <p>Manage business promotion requests from the public</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            onClick={openAddDrawer}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#014d33', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
          >
            <Plus size={18} /> Add Promotion
          </button>
          <div className={styles.searchBar}>
            <Search size={18} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search by business name or contact..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading requests...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length > 0 ? (
                filteredRequests.map(req => (
                  <tr key={req._id}>
                    <td>
                      <div className={styles.adminInfo}>
                        <div className={styles.avatar}>
                          {req.businessName.charAt(0).toUpperCase()}
                        </div>
                        <span>{req.businessName}</span>
                      </div>
                    </td>
                    <td>{req.name}</td>
                    <td>{req.phone || 'N/A'}</td>
                    <td style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {req.description}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge}`} 
                        style={
                          req.status === 'Reviewed' ? { background: '#dcfce7', color: '#16a34a' } : 
                          req.status === 'Dismissed' ? { background: '#fee2e2', color: '#dc2626' } :
                          { background: '#ffedd5', color: '#ea580c' }
                        }>
                        {req.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {req.status !== 'Reviewed' && (
                          <button 
                            onClick={() => handleStatusUpdate(req._id, 'Reviewed')}
                            style={{ color: '#16a34a', background: '#dcfce7', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            title="Mark as Reviewed"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {req.status !== 'Dismissed' && (
                          <button 
                            onClick={() => handleStatusUpdate(req._id, 'Dismissed')}
                            style={{ color: '#ea580c', background: '#ffedd5', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            title="Dismiss Request"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button className={styles.deleteBtn} onClick={() => handleDelete(req._id)} title="Delete Request">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={styles.noData}>No promotion requests found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Promotion Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title="Add Promotion"
      >
        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Business Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. My Cafe" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Category</label>
            <SearchableSelect
              name="category"
              value={formData.category}
              onChange={handleChange}
              placeholder="Select a category"
            >
              <option value="Food & Catering">Food & Catering</option>
              <option value="Health & Wellness">Health & Wellness</option>
              <option value="Education & Coaching">Education & Coaching</option>
              <option value="Fashion & Beauty">Fashion & Beauty</option>
              <option value="Home & Decor">Home & Decor</option>
              <option value="Event & Wedding Services">Event & Wedding Services</option>
              <option value="Professional Services">Professional Services</option>
              <option value="Automotive">Automotive</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Technology & IT">Technology & IT</option>
              <option value="Retail & Shopping">Retail & Shopping</option>
              <option value="Other">Other</option>
            </SearchableSelect>
          </div>
          {formData.category === 'Other' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label>Custom Category</label>
              <input name="customCategory" value={formData.customCategory} onChange={handleChange} required placeholder="Enter custom category" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required placeholder="Tell us about the business" rows="3" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Phone Number</label>
            <input name="phone" value={formData.phone} onChange={handleChange} required placeholder="Contact number" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="business@example.com" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Website Link (Optional)</label>
            <input name="website" value={formData.website} onChange={handleChange} placeholder="e.g. www.mybusiness.com" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Instagram ID / Link (Optional)</label>
            <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="e.g. @mypage" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Facebook ID / Link (Optional)</label>
            <input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="e.g. facebook.com/mypage" style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Banner Image (Max 5MB)</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Logo Image (Max 5MB)</label>
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} />
          </div>
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button type="button" onClick={handleCloseDrawer} style={{ flex: 1, padding: '10px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" style={{ flex: 1, padding: '10px', background: '#014d33', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Add Promotion</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default BusinessPromoRequests;
